"use client";
import { useMemo } from "react";
import { useLocale } from "next-intl";
import { useRouter } from "@i18n/routing";
import { useShallow } from "zustand/react/shallow";
import { useAuth } from "@lib/auth/useAuth";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import {
    getPagePathByLocale,
    shoppingBagPage,
    myAccountPage,
    myAccountWishlistPage,
    storeLocatorPage,
} from "@lib/constants/pagePaths";
import { getLineItemsQuantityWithoutGiftWrapping } from "@lib/utils/getLineItemsQuantityWithoutGiftWrapping";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { useStore } from "@store/index";
import { sizes, space } from "@ecco/ui/tokens";
import { IconButton } from "@ecco/ui/icon-button";
import { Flex } from "@ecco/ui/flex";
import { Box } from "@ecco/ui/box";
import { Circle } from "@ecco/ui/circle";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { NextLink } from "@components/ui/next-link/NextLink";
import { AiChat } from "@features/ai-chat";

const RightNavigation = () => {
    const cart = useStore(useShallow((state) => state.data.me.cartInfo?.cart));
    const wishlist = useStore((state) => state.data.me.wishlistInfo?.wishlist);
    const { push } = useRouter();
    const locale = useLocale();

    const iconHeight = {
        // Need to force overwrite because IconButton inherits it's height from the button sizes definition, which is not made to be overwritten.
        base: sizes.navBarCollapsedHeight + " !important",
    };

    const { isLoggedIn } = useAuth();
    const { showModal } = useGlobalModalContext();

    const productCount = useMemo(
        () => getLineItemsQuantityWithoutGiftWrapping(cart) ?? 0,
        [cart?.lineItems, cart?.totalLineItemQuantity]
    );
    const wishlistCount = useMemo(
        () => wishlist?.lineItems?.filter((item) => item.variant !== null).length || 0,
        [wishlist?.lineItems]
    );

    const handleAccountClick = () => {
        if (!isLoggedIn) return showModal(ModalTypes.createAccount);
        return push(getPagePathByLocale(myAccountPage, locale));
    };
    //TODO: Intl
    const profileLabel = isLoggedIn ? "Go to Your Account" : "Create Account Or Login";

    return (
        <Flex gap={space["0.2"]} data-testid="RightNavigationLayout">
            {/* Account */}
            <IconButton
                data-testid="MenuAccountIcon"
                aria-label={profileLabel}
                variant="tertiary"
                color="black"
                bg="white"
                h={iconHeight}
                w="9" // Accessibility requires a minimum touch target of 44x44px
                icon={<ECCOIcon name={isLoggedIn ? "user-check" : "account"} />}
                onClick={handleAccountClick}
            />
            {/* Store Locator */}
            <IconButton
                data-testid="MenuStoreLocatorIcon"
                as={NextLink}
                href={getPagePathByLocale(storeLocatorPage, locale)}
                aria-label="Store locator"
                variant="tertiary"
                bg="white"
                color="black"
                h={iconHeight}
                w="9" // Accessibility requires a minimum touch target of 44x44px
                display={["none", "none", "none", "none", "flex"]}
                icon={<ECCOIcon name="store-locator" />}
            />
            {/* Wishlist */}
            <IconButton
                data-testid="MenuWishlistIcon"
                as={NextLink}
                href={getPagePathByLocale(myAccountWishlistPage, locale)}
                aria-label={`Wishlist: ${wishlistCount} items`}
                variant="tertiary"
                bg="white"
                color="black"
                h={iconHeight}
                w="9" // Accessibility requires a minimum touch target of 44x44px
                icon={
                    <Box position="relative">
                        <ECCOIcon name="wishlist" />
                        {wishlistCount > 0 && (
                            <Circle
                                size={{ base: "14px", xl: "12px" }}
                                fontSize={{ base: "10px", xl: "8px" }}
                                bg="black"
                                color="white"
                                position="absolute"
                                ml="13px"
                                mt="-13px"
                                fontWeight="extrabold"
                            >
                                {wishlistCount}
                            </Circle>
                        )}
                    </Box>
                }
            />
            {/* Shopping Bag */}
            <IconButton
                data-testid="MenuShoppingBagIcon"
                as={NextLink}
                href={getPagePathByLocale(shoppingBagPage, locale)}
                aria-label={`Shopping bag: ${productCount} items`}
                variant="tertiary"
                bg="white"
                color="black"
                h={iconHeight}
                w="9" // Accessibility requires a minimum touch target of 44x44px
                icon={
                    <Box position="relative">
                        <ECCOIcon name="bag" />
                        {productCount > 0 && (
                            <Circle
                                size={{ base: "14px", xl: "12px" }}
                                fontSize={{ base: "10px", xl: "8px" }}
                                bg="black"
                                color="white"
                                position="absolute"
                                ml="13px"
                                mt="-13px"
                                fontWeight="extrabold"
                            >
                                {productCount}
                            </Circle>
                        )}
                    </Box>
                }
            />
            <AiChat placement="navigation" />
        </Flex>
    );
};

export default RightNavigation;
